import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

// Convertir iconos a componentes React válidos
const Eye = FaEye as React.ComponentType;
const EyeSlash = FaEyeSlash as React.ComponentType;

const ProfileContainer = styled.div`
  max-width: 700px;
  margin: 2rem auto;
  padding: 0;
  background: #FDBF37;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  
  @media (max-width: 768px) {
    margin: 1rem;
    border-radius: 12px;
  }
  
  @media (max-width: 576px) {
    margin: 0.5rem;
    border-radius: 8px;
  }
`;

const Title = styled.h1`
  color: white;
  margin: 0;
  padding: 2rem;
  text-align: center;
  font-size: 2.5rem;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    font-size: 2rem;
    padding: 1.5rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1.8rem;
    padding: 1rem;
  }
`;

const ProfileCard = styled.div`
  background: white;
  margin: 0;
  padding: 2.5rem;
  border-radius: 0;
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
  
  @media (max-width: 576px) {
    padding: 1.5rem;
  }
`;

const ProfileForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #2d3748;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  padding: 1rem 1.2rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #f8fafc;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    background: white;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #f1f5f9;
    color: #64748b;
    cursor: not-allowed;
  }
`;

const PasswordInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PasswordInput = styled(Input)`
  width: 100%;
  padding-right: 3rem;
`;

const EyeIcon = styled.span`
  position: absolute;
  right: 1rem;
  cursor: pointer;
  color: #64748b;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
  
  &:hover {
    color: #667eea;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const SaveButton = styled(Button)`
  background: linear-gradient(135deg, #242A5B 0%, #242A5B 100%);
  color: white;
  
  &:hover {
    background: linear-gradient(135deg, #FDBF37 0%,rgb(251, 191, 55) 100%);
  }
  
  &:disabled {
    background: #a9c7e8;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const CancelButton = styled(Button)`
  background:rgb(249, 241, 241);
  color: #64748b;
  border: 2px solid #e2e8f0;
  
  &:hover {
    background:#FDBF37;
    color: #475569;
  }
`;

const TabContainer = styled.div`
  display: flex;
  margin: 0;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 1.2rem 2rem;
  border: none;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  color: white;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  flex: 1;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 3px solid ${props => props.active ? 'white' : 'transparent'};
  position: relative;
  overflow: hidden;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  @media (max-width: 576px) {
    padding: 1rem;
    font-size: 0.9rem;
  }
`;
const SuccessMessage = styled.div`
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 600;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
const ErrorMessage = styled.div`
  background: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 600;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Profile: React.FC = () => {
  const { user, updateProfile, changePassword, isLoading } = useAuth(); // ← Agregar isLoading
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dni: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        dni: user.dni || ''
      });
    } else if (!isLoading) { 
      navigate('/login');
    }
  }, [user, navigate, isLoading]); 
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateProfile(formData);
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      toast.error('Error al actualizar el perfil');
    }
  };
  
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    try {
      await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      toast.success('Contraseña actualizada correctamente');
    } catch (error) {
      toast.error('Error al cambiar la contraseña');
    }
  };
  
  if (!user) return null;
  
  return (
    <ProfileContainer>
      <Title>Mi Perfil</Title>
      
      <TabContainer>
        <Tab 
          active={activeTab === 'profile'} 
          onClick={() => setActiveTab('profile')}
        >
          Información Personal
        </Tab>
        <Tab 
          active={activeTab === 'password'} 
          onClick={() => setActiveTab('password')}
        >
          Cambiar Contraseña
        </Tab>
      </TabContainer>
      
      <ProfileCard>
        {activeTab === 'profile' ? (
          <ProfileForm onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="name">Nombre</Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="email">Correo Electrónico</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </FormGroup>
            
            <ButtonGroup>
              <SaveButton type="submit">Guardar Cambios</SaveButton>
              <CancelButton type="button" onClick={() => navigate('/')}>Cancelar</CancelButton>
            </ButtonGroup>
          </ProfileForm>
        ) : (
          <ProfileForm onSubmit={handlePasswordSubmit}>
            <FormGroup>
              <Label htmlFor="currentPassword">Contraseña Actual</Label>
              <PasswordInputContainer>
                <PasswordInput
                  type={showCurrentPassword ? "text" : "password"}
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
                <EyeIcon onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                  {showCurrentPassword ? <EyeSlash /> : <Eye />}
                </EyeIcon>
              </PasswordInputContainer>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="newPassword">Nueva Contraseña</Label>
              <PasswordInputContainer>
                <PasswordInput
                  type={showNewPassword ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                />
                <EyeIcon onClick={() => setShowNewPassword(!showNewPassword)}>
                  {showNewPassword ? <EyeSlash /> : <Eye />}
                </EyeIcon>
              </PasswordInputContainer>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
              <PasswordInputContainer>
                <PasswordInput
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                />
                <EyeIcon onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <EyeSlash /> : <Eye />}
                </EyeIcon>
              </PasswordInputContainer>
            </FormGroup>
            
            <ButtonGroup>
              <SaveButton type="submit">Cambiar Contraseña</SaveButton>
              <CancelButton type="button" onClick={() => setActiveTab('profile')}>Cancelar</CancelButton>
            </ButtonGroup>
          </ProfileForm>
        )}
      </ProfileCard>
    </ProfileContainer>
  );
};
export default Profile;